import { TimerType, Timer, now } from "@messages/timing";
import { ScriptsCreationFinished } from "@messages/scripting/ScriptMessages";
import { ScriptTime } from "./script/ScriptTime";
import { ModelData } from "./model/ModelData";
import { uiBusService } from "./services/UIBusService";
const UPDATE_INTERVAL = (1 / 30) * 1000; // Milliseconds
export class Room {
    constructor(context, roomUID) {
        this.context = context;
        this.roomUID = roomUID;
        this.isActive = false;
        this.isReady = false;
        this.models = new Map();
        this.updateCallbacks = new Set();
        this.updateTimer = new Timer();
        this.lastUpdateTime = 0;
        this.numLoadingScripts = 0;
        this.scriptsToBeInitialized = [];
        this.scriptsToBeActivated = []; // onEnterRoom
        this.persistentVariablesChanged = new Set();
        this.isLayoutCreationFinished = false;
        this.firstInitialisationAfterLayouterCreationFinished = false;
        this.update = () => {
            if (!this.isReady || !this.isLayoutCreationFinished)
                return;
            const time = now();
            const deltaTime = (time - this.lastUpdateTime) * 0.001;
            this.lastUpdateTime = time;
            ScriptTime._deltaTime = deltaTime;
            ScriptTime._update();
            this.initScripts();
            if (this.scriptsToBeActivated.length !== 0) {
                for (const script of this.scriptsToBeActivated) {
                    script.onEnterRoom();
                }
                this.scriptsToBeActivated = [];
            }
            if (this.persistentVariablesChanged.size) {
                for (const modelScript of this.persistentVariablesChanged) {
                    modelScript.syncPersistentVariables();
                }
                this.persistentVariablesChanged.clear();
            }
            this.context.flushMessages();
            for (const script of this.updateCallbacks) {
                try {
                    script.onUpdate(deltaTime);
                }
                catch (error) {
                    console.error(error);
                }
            }
            if (this.persistentVariablesChanged.size) {
                for (const modelScript of this.persistentVariablesChanged) {
                    modelScript.syncPersistentVariables();
                }
                this.persistentVariablesChanged.clear();
            }
            this.context.flushMessages();
        };
        this.onLoadScript = () => {
            this.numLoadingScripts++;
        };
        this.onLoadedScript = () => {
            this.numLoadingScripts--;
            if (this.numLoadingScripts <= 0) {
                this.numLoadingScripts = 0;
                if (this.isActive && !this.isReady) {
                    // call after instance created
                    setTimeout(() => {
                        this.init();
                    }, 0);
                }
            }
        };
    }
    init() {
        if (this.numLoadingScripts > 0)
            return;
        this.isReady = true;
        // this.initScripts();
        uiBusService.onRoomInit(this.roomUID);
    }
    initScripts() {
        if (this.scriptsToBeInitialized.length === 0)
            return;
        if (this.isFirstCreation) {
            for (const script of this.scriptsToBeInitialized) {
                script.firstInit();
                if (script.isFirstCreation) {
                    delete script.isFirstCreation;
                }
            }
            this.isFirstCreation = false;
        }
        for (const script of this.scriptsToBeInitialized) {
            if (script.isDestroyed)
                continue;
            if (script.isInitialized)
                continue;
            if (script.isFirstCreation) {
                script.firstInit();
                script.isFirstCreation = false;
            }
            script.init();
        }
        for (const script of this.scriptsToBeInitialized) {
            if (script.isDestroyed)
                continue;
            script.start();
        }
        this.scriptsToBeInitialized = [];
        if (this.firstInitialisationAfterLayouterCreationFinished) {
            this.firstInitialisationAfterLayouterCreationFinished = false;
            this.context.pendingMessages.push(new ScriptsCreationFinished(this.roomUID));
            this.context.flushMessages();
        }
    }
    onLayoutUpdated() {
        this.scriptsToBeInitialized = [];
        this.scriptsToBeActivated = [];
        if (this.isFirstCreation) {
            this.isFirstCreation = false;
        }
        this.persistentVariablesChanged.clear();
        this.isLayoutCreationFinished = false;
        this.isReady = false;
    }
    onLayoutCreationFinished(isFirstCreation) {
        this.isLayoutCreationFinished = true;
        this.firstInitialisationAfterLayouterCreationFinished = true;
        if (isFirstCreation) {
            this.isFirstCreation = isFirstCreation;
        }
        if (this.isActive) {
            for (const model of this.models.values()) {
                for (const script of model.scripts.values()) {
                    if (script.isReady()) {
                        this.scriptsToBeInitialized.push(script);
                        this.scriptsToBeActivated.push(script);
                    }
                }
            }
        }
    }
    onEnterRoom() {
        this.isActive = true;
        if (this.isLayoutCreationFinished) {
            for (const model of this.models.values()) {
                for (const script of model.scripts.values()) {
                    if (script.isReady()) {
                        this.scriptsToBeInitialized.push(script);
                        this.scriptsToBeActivated.push(script);
                    }
                }
            }
        }
        if (!this.updateTimer.isPlanned()) {
            this.lastUpdateTime = now();
            this.updateTimer.setup(this.update, UPDATE_INTERVAL, TimerType.Interval);
        }
    }
    onLeaveRoom() {
        this.isActive = false;
        this.updateTimer.cancel();
        this.scriptsToBeActivated = [];
        for (const model of this.models.values()) {
            for (const script of model.scripts.values()) {
                script.onLeaveRoom();
            }
        }
    }
    addScript(script) {
        let model = this.models.get(script.modelUID);
        if (model === undefined) {
            this.models.set(script.modelUID, (model = new ModelData(script.modelUID, this)));
        }
        // ToDo: remove old script?
        model.scripts.set(script.scriptId, script);
        if (this.isLayoutCreationFinished) {
            script.isFirstCreation = true;
        }
    }
    removeScript(script) {
        const model = this.models.get(script.modelUID);
        if (!model)
            return;
        if (model.scripts.delete(script.scriptId) && model.scripts.size === 0) {
            this.models.delete(script.modelUID);
        }
    }
    registerUpdateCallback(script) {
        if (this.isActive && !this.updateTimer.isPlanned()) {
            this.lastUpdateTime = now();
            this.updateTimer.setup(this.update, UPDATE_INTERVAL, TimerType.Interval);
        }
        this.updateCallbacks.add(script);
    }
    unregisterUpdateCallback(script) {
        this.updateCallbacks.delete(script);
        if (this.updateCallbacks.size === 0) {
            this.updateTimer.cancel();
        }
    }
}
